import qs from 'query-string'
import request from 'superagent'
import moment from 'moment'
import matchMedia from 'matchmedia'
import {
  orderBy,
  find,
  filter,
  get,
  identity,
  uniq,
  toPairs,
  pick,
  omit,
  isEmpty,
  isArray,
  isPlainObject,
  mapValues,
  map,
  startsWith,
} from 'lodash'
import { defaultMemoize } from 'reselect'
import autoprefixer from 'material-ui/utils/autoprefixer'
import breakpoints from '../styles/breakpoints'

export { default as shallowEqual } from './shallowEqual'

const isPositiveInt = i => !isNaN(i) && i > 0

// Parse single qs argument
export const parseQsArg = (
  location,
  key,
  defaultValue = '',
  map = identity
) => {
  const value = get(qs.parse(qs.extract(location.search)), key, defaultValue)
  return value === defaultValue ? value : map(value)
}

// Parse ?q=SomeSearchString
export const parseQ = location => parseQsArg(location, 'q')

export const parseQsGenere = location => parseQsArg(location, 'genere')

// Parse json attributes from string, keep only whitelisted...
const jsonParseAttrs = (rowAttrs, whitelist) => {
  let parsedAttr
  try {
    parsedAttr = JSON.parse(rowAttrs)
  } catch (e) {
    parsedAttr = {}
  }

  const attrs = isPlainObject(parsedAttr) ? parsedAttr : {}

  return isArray(whitelist) && whitelist.length > 0
    ? pick(attrs, whitelist)
    : attrs
}

export const parseQsAttributes = (location, whitelist) => {
  const rowAttrs = parseQsArg(location, 'attributi', '{}')
  return jsonParseAttrs(rowAttrs, whitelist)
}

export const makeParseQsAttributesMem = () => {
  const memJsonParseAtts = defaultMemoize(jsonParseAttrs)
  return (location, whitelist) => {
    const rowAttrs = parseQsArg(location, 'attributi', '{}')
    return memJsonParseAtts(rowAttrs, whitelist)
  }
}

export const makeParseQsAttributesValuesMem = () => {
  const parseQsAttributes = makeParseQsAttributesMem()
  const memAttrsValues = defaultMemoize(attrs =>
    map(attrs, (valore, chiave) => ({
      chiave,
      valore,
    }))
  )
  return (location, whitelist) =>
    memAttrsValues(parseQsAttributes(location, whitelist))
}

export const getFilterAttributesByCategory = defaultMemoize(category => {
  return orderBy(category.attributi.filter(attr => attr.filter), ['position'])
})

// Parse ?items=id,id,id as an array
export const parseQsItems = (location, key) =>
  parseQsArg(location, key, [], i =>
    uniq(
      i
        .split(',')
        .map(i => +i)
        .filter(i => isPositiveInt(i))
    )
  )

// Parse ?money=$$$
export const parseQsMoney = (location, key) =>
  parseQsArg(location, key, 0, m => (isPositiveInt(parseInt(m, 10)) ? +m : 0))

// Parse ?id=<id> return valid id parsed as int or null
export const parseQsId = (location, key) =>
  parseQsArg(
    location,
    key,
    null,
    m => (isPositiveInt(parseInt(m, 10)) ? +m : null)
  )

// Parse ?type=proposte|accettate|archivio
// export const parseQsType = (location) => {
//   const type = parseQsArg(location, 'type', 'proposte');
//   const validTypes = ['proposte', 'accettate', 'archivio'];
//   if (validTypes.indexOf(type) === -1) {
//     return 'proposte';
//   }
//   return type;
// };

// TODO: Maybe pick only filters neeeded....
export const mergeQs = (location, o) =>
  // New search merge current qs with new object as qs...
  qs.stringify({
    ...qs.parse(qs.extract(location.search)),
    ...o,
  })

// Simply version of qs.stringify
export const objToQs = o =>
  toPairs(o)
    .map(p => `${p[0]}=${p[1]}`)
    .join('&')

export const isNullOrUndef = a => a === null || typeof a === 'undefined'

export const itemLink = (item, givenItem) => {
  const link = `/item/${item.id}`
  const userId = get(item, 'posseduto_da[0].user.id')
  const queryString = qs.stringify({
    userId,
    givenItem,
  })
  if (queryString === '') {
    return link
  }
  return `${link}?${queryString}`
}

const objConditions = {
  default: [
    { value: 0, label: 'Scarse: Molto vissuto ma ancora utilizzabile' },
    { value: 1, label: 'Buone: utilizzato più volte ma ancora accettabile' },
    { value: 2, label: 'Ottime: Usato una sola volta e messo via' },
    { value: 3, label: 'Nuovo: Mai utilizzato' },
  ],

  50: [
    { value: 0, label: 'Fair (F)' },
    { value: 1, label: 'Poor (P)' },
    { value: 2, label: 'Good (G)' },
    { value: 3, label: 'Good Plus (G+)' },
    { value: 4, label: 'Very Good (VG)' },
    { value: 5, label: 'Very Good Plus (VG+)' },
    { value: 6, label: 'Near Mint (NM)' },
    { value: 7, label: 'Mint (M)' },
  ],
}

export const getObjectConditions = catId =>
  objConditions[parseInt(catId, 10)] || objConditions['default']

export const getObjectCondition = (catId, conditionIndex) => {
  const conditions = getObjectConditions(catId)
  const condition = find(conditions, ({ value }) => +value === +conditionIndex)
  if (condition) {
    return condition.label
  }
  return null
}

export const getObjectOwner = defaultMemoize((obj, ownerId) => {
  if (!obj.posseduto_da || !obj.posseduto_da.length) {
    return null
  }
  return find(obj.posseduto_da, x => x.user && x.user.id === ownerId)
})

// memoize: same instance -> same result no fear to use in render :P

export const getProponenteItems = defaultMemoize(swap =>
  swap.oggetti.filter(
    item =>
      typeof item.oggetto !== 'undefined' && item.utente === swap.proponente.id
  )
)

export const getDestinatarioItems = defaultMemoize(swap =>
  swap.oggetti.filter(
    item =>
      typeof item.oggetto !== 'undefined' &&
      item.utente === swap.destinatario.id
  )
)

export const getProponenteCash = defaultMemoize(swap =>
  swap.oggetti.reduce((cash, item) => {
    if (
      typeof item.ecoin !== 'undefined' &&
      item.utente === swap.proponente.id
    ) {
      return cash + item.ecoin
    }
    return cash
  }, 0)
)

export const getDestinatarioCash = defaultMemoize(swap =>
  swap.oggetti.reduce((cash, item) => {
    if (
      typeof item.ecoin !== 'undefined' &&
      item.utente === swap.destinatario.id
    ) {
      return cash + item.ecoin
    }
    return cash
  }, 0)
)

export const getDestinatarioEsito = defaultMemoize(swap =>
  get(
    get(swap, 'esiti', []).filter(e => e.utente === swap.destinatario.id),
    '[0]'
  )
)

export const getProponenteEsito = defaultMemoize(swap =>
  get(
    get(swap, 'esiti', []).filter(e => e.utente === swap.proponente.id),
    '[0]'
  )
)

export const isSwapValid = defaultMemoize(
  swap =>
    !// No items destinatario
    (
      swap.destinatarioItems.length === 0 ||
      // Give nothing
      (swap.proponenteItems.length === 0 && swap.proponenteMoney === 0)
    )
)

// Is user proponente of swap
export const isProponente = (swap, user) => swap.proponente.id === user.id

// Is user destinatario of swap
export const isDestinatario = (swap, user) => swap.destinatario.id === user.id

// Thanks to Mauro \+.+/

const proponenteSwap = swap => ({
  ...swap,
  user: swap.proponente,
  money: swap.proponenteMoney,
  items: swap.proponenteItems,
  itemsIds: swap.proponenteItemsIds,
  itemsData: swap.proponenteItemsData,
  esito: swap.proponenteEsito,
})

const destinatarioSwap = swap => ({
  ...swap,
  user: swap.destinatario,
  money: swap.destinatarioMoney,
  items: swap.destinatarioItems,
  itemsIds: swap.destinatarioItemsIds,
  itemsData: swap.destinatarioItemsData,
  esito: swap.destinatarioEsito,
})

export const getSwapForMe = defaultMemoize(
  (swap, user) =>
    isProponente(swap, user) ? proponenteSwap(swap) : destinatarioSwap(swap)
)

export const getSwapForYou = defaultMemoize(
  (swap, user) =>
    isDestinatario(swap, user) ? proponenteSwap(swap) : destinatarioSwap(swap)
)

// > 90 giorni
export const isSwapOutdated = defaultMemoize(
  swap => moment().diff(moment(swap.updated_at), 'days') >= 90
)

const affidabiltaBands = [
  { from: 0, to: 0, rank: 0, label: 'Utente inattivo (o Utente nuovo)' },
  { from: 1, to: 69, rank: 1, label: 'Inaffidabile (o Utente poco attivo)' },
  { from: 70, to: 79, rank: 2, label: 'Poco affidabile' },
  { from: 80, to: 89, rank: 3, label: 'Non sempre affidabile' },
  { from: 90, to: 95, rank: 4, label: 'Abbastanza affidabile' },
  { from: 96, to: 100, rank: 5, label: 'Affidabile' },
]

const getAffidabilitaBandByPercent = percent =>
  find(affidabiltaBands, ({ from, to }) => percent >= from && percent <= to)

const normalizeAffidabilita = affidabilita =>
  Math.min(Math.max(Math.round(affidabilita), 0), 100)

export const getSwapShippingInfo = defaultMemoize(swap => {
  const proponenteBand = getAffidabilitaBandByPercent(
    normalizeAffidabilita(swap.proponente.affidabilita * 100)
  )
  const destinatarioBand = getAffidabilitaBandByPercent(
    normalizeAffidabilita(swap.destinatario.affidabilita * 100)
  )

  if (proponenteBand.rank < destinatarioBand.rank) {
    // Spedisce per primo proponente
    return { user: swap.proponente, reason: 'affidabilita' }
  } else if (proponenteBand.rank > destinatarioBand.rank) {
    // Spedisce per primo destinatario
    return { user: swap.destinatario, reason: 'affidabilita' }
  } else {
    if (swap.proponente.transazioni > swap.destinatario.transazioni) {
      // Spedisce per primo destinatario
      return { user: swap.destinatario, reason: 'transazioni' }
    } else if (swap.proponente.transazioni < swap.destinatario.transazioni) {
      // Spedisce per primo proponente
      return { user: swap.proponente, reason: 'affidabilita' }
    } else {
      // Nessuno deve spedire per primo
      return null
    }
  }
})

export const prepareNewItemForApi = newItem => {
  const newItemForApi = {
    ...newItem,
    cat_id: newItem.category.id,
    attributi: filter(newItem.attributi, attr => !isEmpty(attr)),
  }
  return omit(newItemForApi, ['category', 'world'])
}

// Sum valore of given items
export const totalItemsValue = items =>
  items.reduce((r, i) => r + Number(get(i, 'posseduto_da[0].valore', 0)), 0)

export const getCategoryCardImage = (worldSlug, catergorySlug) => {
  return `/categories-img/${worldSlug}/${catergorySlug}.jpg`
}

// Utente può scambiare solo quando ha l'indirizzo
export const checkUserCanSwap = user => {
  if (!user) {
    return false
  }
  return !!(
    user.address &&
    user.zip &&
    user.city &&
    user.district &&
    user.firstname &&
    user.lastname
  )
}

export function getBase64ImageUrl(url, callback) {
  return request
    .get(url)
    .responseType('blob')
    .then(response => {
      // TODO: Find a way to handle reject in another life
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = e => {
          resolve(reader.result)
        }
        reader.readAsDataURL(response.body)
      })
    })
}

// Make extra easy prevent default in React Yesssa
// onClick={callPreventDefault(doSomeCoolStuff)}
export const callPreventDefault = fn => e => {
  e.preventDefault()
  e.stopPropagation()
  fn()
}

export const isInteger =
  typeof Number.isInteger === 'undefined'
    ? // isInteger compatible with IE10
      num => (num ^ 0) === num
    : // Normal isInteger implementation
      Number.isInteger

// Atuoprefix inline css
export const autoprefixCss = autoprefixer({})

// Apply auto prefix to all key of given object util for
// autoprefixing all style...
export const makeStyles = style => mapValues(style, autoprefixCss)

// Check if given id is an external item id: discogs_123
export const isExternalItemId = id =>
  isInteger(id) ? false : `${id}`.split('_').length > 1

const START_KEY = '__start_the_odissea__'
export const getLocationKey = location => get(location, 'key', START_KEY)

export const isUrl = url => {
  const regexp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/
  return regexp.test(url)
}

export const isValidImage = img => {
  if (typeof img !== 'string') return false
  return isUrl(img) || img.indexOf('data:image') === 0
}

export const isOnMobile = () =>
  matchMedia(`screen and (max-width: ${breakpoints.xs})`).matches

export const adjustScrollToIndex = (index, count) => {
  // On mobile simply the index
  if (isOnMobile()) {
    return index
  }
  // this make prev clikked item in center of list..
  return Math.min(count - 1, index + 2)
}

const S3_IMAGE_PREFIX = 'https://s3.eu-central-1.amazonaws.com/swapick.storage'

export const isManualImage = imageUrl => startsWith(imageUrl, S3_IMAGE_PREFIX)

export const getImageUrlRes = (imageUrl, resolution = null) => {
  if (!resolution || !imageUrl) {
    return imageUrl
  }

  if (isManualImage(imageUrl)) {
    const imgs = imageUrl.split('.')
    imgs[imgs.length - 2] += `@${resolution}`
    return imgs.join('.')
  }

  return imageUrl
}

const GENERE_FILTER_CATEGORIES = [1, 2, 3, 4, 50]

export const shouldShowGenereFilterByCategoryId = categoryId => {
  return GENERE_FILTER_CATEGORIES.indexOf(categoryId) !== -1
}

export const shouldShowGenereFilter = category => {
  if (!category) {
    return false
  }
  return shouldShowGenereFilterByCategoryId(+category.id)
}

export const getFiltersChunkedSize = () => {
  if (matchMedia(`screen and (max-width: ${breakpoints.xs})`).matches) {
    // This mean no chunk :/
    return 10000
  } else if (matchMedia(`screen and (max-width: ${breakpoints.sm})`).matches) {
    return 3
  } else if (matchMedia(`screen and (max-width: ${breakpoints.md})`).matches) {
    return 3
  } else {
    return 4
  }
}

export const getImmaginiOfUser = defaultMemoize((immagini = [], userId) => {
  return immagini.filter(img => +img.user_id === +userId)
})



// WEBPACK FOOTER //
// ./src/utils/index.js